import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom-v6'
import { Bot, Building2, KeyRound, Settings, ShieldCheck, Users } from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@clouway/ui'

import { Permission, hasPermission, useSession } from '@/features/auth'

// i18n keys (common namespace) rather than literals — the app is bg-primary.
// `permission` is the list permission that gates the item (accountSettings has
// no list verb, so it gates on get instead); the server still enforces the
// real authorization, this only hides what the caller cannot use.
const sections = [
  {
    labelKey: 'drawer.sections.organization',
    items: [
      {
        titleKey: 'drawer.items.users',
        url: '/',
        icon: Users,
        permission: Permission.users.list,
        isActive: (pathname: string) => pathname === '/' || pathname.startsWith('/users'),
      },
      {
        titleKey: 'drawer.items.roles',
        url: '/roles',
        icon: ShieldCheck,
        permission: Permission.roles.list,
        isActive: (pathname: string) => pathname.startsWith('/roles'),
      },
      {
        titleKey: 'drawer.items.company',
        url: '/company',
        icon: Building2,
        permission: Permission.customers.list,
        isActive: (pathname: string) => pathname.startsWith('/company'),
      },
    ],
  },
  {
    labelKey: 'drawer.sections.integrations',
    items: [
      {
        titleKey: 'drawer.items.apis',
        url: '/apis',
        icon: KeyRound,
        permission: Permission.serviceAccounts.list,
        isActive: (pathname: string) => pathname.startsWith('/apis'),
      },
      {
        titleKey: 'drawer.items.serviceAccounts',
        url: '/service-accounts',
        icon: Bot,
        permission: Permission.serviceAccounts.list,
        isActive: (pathname: string) => pathname.startsWith('/service-accounts'),
      },
      {
        titleKey: 'drawer.items.settings',
        url: '/settings',
        icon: Settings,
        permission: Permission.accountSettings.get,
        isActive: (pathname: string) => pathname.startsWith('/settings'),
      },
    ],
  },
] as const

function AppSidebar() {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const { access } = useSession()
  // On mobile the sidebar renders as an overlay sheet (Sidebar wraps it in a
  // Radix Sheet when isMobile) — without this it stays open over the newly
  // navigated page until the admin taps the overlay themselves. Desktop
  // rendering ignores openMobile entirely, so this is a no-op there.
  const { isMobile, setOpenMobile } = useSidebar()
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false)
  }

  // Sections collapse to the items the caller's permissions allow; an empty
  // section is hidden entirely.
  const visibleSections = sections
    .map(section => ({
      ...section,
      items: section.items.filter(item => hasPermission(access, item.permission)),
    }))
    .filter(section => section.items.length > 0)

  return (
    <Sidebar>
      <SidebarHeader>
        <span className="px-2 py-1.5 text-sm font-semibold">CW Cloud Console</span>
      </SidebarHeader>
      <SidebarContent>
        {visibleSections.map(section => (
          <SidebarGroup key={section.labelKey}>
            <SidebarGroupLabel>{t(section.labelKey)}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map(item => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={item.isActive(pathname)}>
                      <Link to={item.url} onClick={closeOnMobile}>
                        <item.icon />
                        <span>{t(item.titleKey)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  )
}

export default AppSidebar
